import pipefitterApi from '../../../services/pipefitterApi';
import type { PipeStatusItem } from '../types';

/** Filter payload aligned with fetchPipeStatusSaga / PipeFilter dispatch */
export interface PipeStatusListFilterPayload {
  project_id?: string | number | null;
  projectId?: string | number | null;
  pipe_id?: string | null;
  pipeId?: string | null;
  project_num?: string | null;
  projectNumber?: string | null;
  project_name?: string | null;
  projectName?: string | null;
}

export interface PipeStatusListPagination {
  skip?: number;
  limit?: number;
}

function appendPagination(params: URLSearchParams, pagination?: PipeStatusListPagination) {
  if (!pagination) return;
  if (typeof pagination.skip === 'number' && Number.isFinite(pagination.skip) && pagination.skip >= 0) {
    params.set('skip', String(Math.floor(pagination.skip)));
  }
  if (typeof pagination.limit === 'number' && Number.isFinite(pagination.limit)) {
    const lim = Math.min(500, Math.max(1, Math.floor(pagination.limit)));
    params.set('limit', String(lim));
  }
}

/**
 * Query params for GET `/v1/reports/getPipeStatusList` (same order as backend contract), e.g.:
 * `?project_num=YOUR_PROJECT_NUMBER&deleted=0&skip=0&limit=50`
 * Optional filters (`project_id`, `pipe_id`, `project_name`) are appended after when present.
 */
export function buildPipeStatusListQueryParams(
  payload: PipeStatusListFilterPayload,
  pagination?: PipeStatusListPagination
): URLSearchParams {
  const projectId = payload?.project_id ?? payload?.projectId;
  const projectNum = payload?.project_num ?? payload?.projectNumber;
  const projectName = payload?.project_name ?? payload?.projectName ?? '';
  const pipeId = payload?.pipe_id ?? payload?.pipeId;

  const params = new URLSearchParams();
  if (projectNum != null && String(projectNum).trim() !== '') {
    params.append('project_num', String(projectNum).trim());
  }
  params.append('deleted', '0');
  if (pagination && (pagination.skip !== undefined || pagination.limit !== undefined)) {
    appendPagination(params, pagination);
  }
  if (projectId != null && projectId !== '') params.append('project_id', String(projectId));
  if (pipeId != null && pipeId !== '') params.append('pipe_id', String(pipeId));
 // if (projectName !== '') params.append('project_name', String(projectName));

  return params;
}

export function getPipeStatusListUrl(query: URLSearchParams): string {
  return `/reports/getPipeStatusList?${query.toString()}`;
}

export interface ParsedPipeStatusListResponse {
  rows: PipeStatusItem[];
  totalCount: number | undefined;
}

const ROW_KEYS = ['data', 'records', 'results', 'items', 'pipeStatusList', 'pipeDataList', 'list', 'rows'] as const;
const NESTED_ROW_KEYS = ['records', 'results', 'items', 'data', 'rows', 'list'] as const;
const TOTAL_KEYS = ['totalCount', 'total', 'count', 'totalRecords'] as const;

function readNumericField(obj: Record<string, unknown>, keys: readonly string[]): number | undefined {
  for (const key of keys) {
    const v = obj[key];
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    if (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v))) return Number(v);
  }
  return undefined;
}

/** Collect objects to scan for rows / totals (root + optional nested `data` object). */
function responseBodyCandidates(body: unknown): Record<string, unknown>[] {
  if (!body || typeof body !== 'object' || Array.isArray(body)) return [];
  const root = body as Record<string, unknown>;
  const out: Record<string, unknown>[] = [root];
  const inner = root.data;
  if (inner && typeof inner === 'object' && !Array.isArray(inner)) {
    out.push(inner as Record<string, unknown>);
  }
  return out;
}

/** Rows from getPipeStatusList-style envelopes (several backend field names). */
export function extractPipeStatusListRows(body: unknown): PipeStatusItem[] {
  if (Array.isArray(body)) return body as PipeStatusItem[];
  for (const b of responseBodyCandidates(body)) {
    for (const key of ROW_KEYS) {
      const v = b[key];
      if (Array.isArray(v)) return v as PipeStatusItem[];
      if (v && typeof v === 'object' && !Array.isArray(v)) {
        const nested = v as Record<string, unknown>;
        for (const nk of NESTED_ROW_KEYS) {
          const nv = nested[nk];
          if (Array.isArray(nv)) return nv as PipeStatusItem[];
        }
      }
    }
  }
  return [];
}

function extractPipeStatusListTotalCount(body: unknown): number | undefined {
  for (const b of responseBodyCandidates(body)) {
    const n = readNumericField(b, [...TOTAL_KEYS]);
    if (n !== undefined) return n;
  }
  return undefined;
}

/** True if body uses a known list-shaped field (even when the list is empty). */
function responseDeclaresPipeListField(body: unknown): boolean {
  if (!body || typeof body !== 'object' || Array.isArray(body)) return false;
  for (const b of responseBodyCandidates(body)) {
    for (const key of ROW_KEYS) {
      const v = b[key];
      if (Array.isArray(v)) return true;
      if (v && typeof v === 'object' && !Array.isArray(v)) {
        const nested = v as Record<string, unknown>;
        for (const nk of NESTED_ROW_KEYS) {
          if (Array.isArray(nested[nk])) return true;
        }
      }
    }
  }
  return false;
}

/**
 * Whether the getPipeStatusList response body should be treated as success.
 * Backends differ: some use `status: 200`, others `success: true`, or only return rows.
 */
export function isGetPipeStatusListSuccess(body: unknown): boolean {
  if (Array.isArray(body)) return true;
  if (!body || typeof body !== 'object') return false;
  const b = body as Record<string, unknown>;
  // Some backends use 304 in the JSON envelope the same way as 200 (not HTTP Not Modified).
  if (
    b.status === 200 ||
    b.status === 304 ||
    b.statusCode === 200 ||
    b.statusCode === 304 ||
    b.code === 200 ||
    b.code === 304
  )
    return true;
  if (b.success === true || b.success === 1) return true;
  const rows = extractPipeStatusListRows(body);
  if (rows.length > 0) return true;
  const tc = extractPipeStatusListTotalCount(body);
  if (tc === 0) return true;
  if (responseDeclaresPipeListField(body)) return true;
  return false;
}

/** Parse axios response body for getPipeStatusList (rows + optional totalCount). */
export function parsePipeStatusListResponse(res: { data?: unknown }): ParsedPipeStatusListResponse {
  const body = res?.data;
  const rows = extractPipeStatusListRows(body);
  const totalCount = extractPipeStatusListTotalCount(body);
  return { rows, totalCount };
}

/** Full list for modals — one large page (backend caps limit at 500). */
export async function fetchPipeStatusListUnpaginated(
  filterPayload: PipeStatusListFilterPayload
): Promise<PipeStatusItem[]> {
  const params = buildPipeStatusListQueryParams(filterPayload, { skip: 0, limit: 500 });
  const { data } = await pipefitterApi.get(getPipeStatusListUrl(params));
  if (!isGetPipeStatusListSuccess(data)) {
    return [];
  }
  return parsePipeStatusListResponse({ data }).rows;
}
